import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Info, X } from 'lucide-react';

/**
 * Modale Art. 14 RGPD — Information sur la collecte indirecte de donnees (annuaire clubs).
 * Reutilisable sur /clubs (annuaire) et /clubs/:id (fiche club).
 */

interface Art14ClubModalProps {
  trigger: React.ReactNode;
}

export function Art14ClubModal({ trigger }: Art14ClubModalProps) {
  const [open, setOpen] = useState(false);

  return (
    <>
      <span onClick={() => setOpen(true)} className='cursor-pointer'>
        {trigger}
      </span>

      {open && (
        <div className='fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4'>
          <div className='w-full max-w-2xl rounded-2xl bg-white shadow-2xl max-h-[85vh] overflow-y-auto'>
            {/* Header */}
            <div className='sticky top-0 z-10 bg-white px-6 py-4 border-b border-slate-200 flex items-center justify-between'>
              <div className='flex items-center gap-2'>
                <Info className='w-5 h-5 text-blue-600' />
                <h2 className='text-lg font-bold text-slate-900'>Information sur la collecte de donnees</h2>
              </div>
              <button
                onClick={() => setOpen(false)}
                className='rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition-colors'
              >
                <X className='w-5 h-5' />
              </button>
            </div>

            {/* Content */}
            <div className='px-6 py-5 space-y-5 text-sm text-slate-700 leading-relaxed'>
              <div>
                <h3 className='font-bold text-slate-900 mb-1'>1. Qui collecte ces donnees ?</h3>
                <p>
                  MADSAAS SAS, editrice de la plateforme BoulesConnect, societe par actions simplifiee au capital de 1
                  000 euros, immatriculee au RCS de Gap sous le numero 821 350 600, dont le siege social est situe 05110
                  Lardier-et-Valenca.
                </p>
                <p className='mt-1'>
                  Contact DPO :{' '}
                  <Link to='/contact' className='text-blue-600 hover:underline'>
                    Formulaire de contact - Sujet : Données personnelles
                  </Link>
                </p>
              </div>

              <div>
                <h3 className='font-bold text-slate-900 mb-1'>2. Quelles donnees et d&apos;ou proviennent-elles ?</h3>
                <p>
                  Nous collectons, a partir de l&apos;annuaire public de la Federation Francaise de Petanque et Jeu
                  Provencal (ffpjp.org) et de la Federation Francaise de Boule Lyonnaise (ffbl.fr), les informations
                  suivantes concernant les clubs affilies : nom du club, numero d&apos;affiliation, adresse, coordonnees
                  de contact (email, telephone, site web), disciplines pratiquees, effectif de licencies (agrege) et, le
                  cas echeant, nom et coordonnees professionnelles du president ou du secretaire lorsqu&apos;ils ont ete
                  rendus publics par le club sur les sites des federations.
                </p>
              </div>

              <div>
                <h3 className='font-bold text-slate-900 mb-1'>3. Pour quelles finalites ?</h3>
                <p>
                  Ces donnees permettent (a) aux joueurs et visiteurs de rechercher un club par zone geographique et
                  discipline, (b) aux organisateurs de concours de contacter les clubs pour leurs evenements, (c) aux
                  clubs eux-memes de revendiquer et enrichir leur fiche une fois inscrits sur BoulesConnect.
                </p>
              </div>

              <div>
                <h3 className='font-bold text-slate-900 mb-1'>4. Sur quelle base legale ?</h3>
                <p>
                  Article 6.1.f du RGPD — interet legitime de MADSAAS SAS a proposer un annuaire national des clubs et
                  des utilisateurs a y acceder. Une analyse d&apos;interet legitime (LIA) a ete documentee et est
                  disponible sur demande a{' '}
                  <Link to='/contact' className='text-blue-600 hover:underline'>
                    Formulaire de contact - Sujet : Données personnelles
                  </Link>
                  .
                </p>
              </div>

              <div>
                <h3 className='font-bold text-slate-900 mb-1'>5. Qui sont les destinataires ?</h3>
                <p>
                  Les donnees de l&apos;annuaire sont publiques sur la plateforme BoulesConnect. Elles ne sont pas
                  cedees a des tiers a des fins commerciales. Hebergement : OVH SAS (Roubaix et Gravelines, France).
                </p>
              </div>

              <div>
                <h3 className='font-bold text-slate-900 mb-1'>6. Combien de temps sont-elles conservees ?</h3>
                <p>
                  Tant que le club figure dans l&apos;annuaire officiel de sa federation. En cas de depart de la
                  federation, de demande d&apos;opposition ou d&apos;effacement, les donnees sont supprimees sous 30
                  jours (1 mois — delai Art. 12.3 RGPD).
                </p>
              </div>

              <div>
                <h3 className='font-bold text-slate-900 mb-1'>7. Quels sont vos droits ?</h3>
                <p>
                  Conformement aux articles 15 a 22 du RGPD, vous disposez d&apos;un droit d&apos;acces, de
                  rectification, d&apos;effacement, de limitation, d&apos;opposition et de portabilite sur vos donnees.
                  Vous pouvez exercer ces droits a tout moment en ecrivant a{' '}
                  <Link to='/contact' className='text-blue-600 hover:underline'>
                    Formulaire de contact - Sujet : Données personnelles
                  </Link>{' '}
                  ou en utilisant le bouton « Retirer ma fiche » present sur chaque page de club.
                </p>
                <p className='mt-2'>
                  En cas de difficulte, vous pouvez introduire une reclamation aupres de la{' '}
                  <a
                    href='https://www.cnil.fr/fr/plaintes'
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-blue-600 hover:underline'
                  >
                    CNIL
                  </a>{' '}
                  (3 place de Fontenoy, TSA 80715, 75334 Paris Cedex 07).
                </p>
              </div>

              <div>
                <h3 className='font-bold text-slate-900 mb-1'>8. Pas de decision automatisee</h3>
                <p>
                  Aucune decision produisant des effets juridiques ou vous affectant de maniere significative n&apos;est
                  prise de maniere automatisee a partir de ces donnees (Art. 22 RGPD).
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className='px-6 py-4 border-t border-slate-200 bg-slate-50 rounded-b-2xl'>
              <button
                onClick={() => setOpen(false)}
                className='w-full rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 transition-colors'
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
